import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { toast } from 'sonner';
import { adminApi } from '../lib/adminAuth';
import {
  Users, Building2, AlertTriangle, CheckCircle2, TrendingUp,
  ChevronDown, ChevronUp, Search, Filter, RefreshCw, Download, ArrowUpDown,
  UserPlus, X, Check, AlertCircle, BarChart3, Loader2, XCircle,
  ArrowUp, ArrowDown, Eye
} from 'lucide-react';
import { HeaderSkeleton, MetricCardsSkeleton, TableSkeleton } from './skeletons/Skeletons';
import { getTodayLocal } from '../lib/dateUtils';

// ─── Shared Components ────────────────────────────────────────

const WorkloadBadge = ({ level, score }) => {
  const config = {
    low: { bg: 'bg-emerald-50 border-emerald-200', text: 'text-emerald-700', label: 'Low' },
    medium: { bg: 'bg-amber-50 border-amber-200', text: 'text-amber-700', label: 'Medium' },
    high: { bg: 'bg-red-50 border-red-200', text: 'text-red-700', label: 'High' }
  };
  const { bg, text, label } = config[level] || config.medium;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-xl text-xs font-semibold border ${bg} ${text}`}>
      {label} <span className="opacity-60">({score})</span>
    </span>
  );
};

const ProgressBar = ({ value, max = 100, color = 'pink' }) => {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  const cls = { pink: 'bg-pink-500', green: 'bg-emerald-500', yellow: 'bg-amber-500', red: 'bg-red-500', blue: 'bg-blue-500' }[color] || 'bg-pink-500';
  return (
    <div className="w-full bg-neutral-100 rounded-full h-1.5">
      <div className={`${cls} h-1.5 rounded-full transition-all duration-500`} style={{ width: `${pct}%` }} />
    </div>
  );
};

const SortHeader = ({ label, sortKey, sortConfig, onSort, className = '', align = 'left' }) => {
  const isActive = sortConfig.key === sortKey;
  const alignCls = align === 'center' ? 'justify-center' : 'justify-start';
  return (
    <button
      onClick={() => onSort(sortKey)}
      className={`flex items-center gap-1 font-semibold text-neutral-600 hover:text-[#1a1a2e] transition-colors group ${alignCls} w-full ${className}`}
      data-testid={`sort-${sortKey}`}
    >
      <span className="text-xs uppercase tracking-wide">{label}</span>
      <span className="flex flex-col -space-y-1">
        <ArrowUp className={`w-3 h-3 ${isActive && sortConfig.direction === 'asc' ? 'text-pink-600' : 'text-neutral-300 group-hover:text-neutral-400'}`} />
        <ArrowDown className={`w-3 h-3 ${isActive && sortConfig.direction === 'desc' ? 'text-pink-600' : 'text-neutral-300 group-hover:text-neutral-400'}`} />
      </span>
    </button>
  );
};

const FilterChip = ({ label, active, onClick, count }) => (
  <button
    onClick={onClick}
    className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full border transition-all ${
      active
        ? 'bg-pink-50 border-pink-300 text-pink-700 shadow-soft'
        : 'bg-white border-white/40 text-neutral-600 hover:bg-white/60 hover:border-white/40'
    }`}
  >
    {label}
    {count !== undefined && (
      <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${active ? 'bg-pink-200 text-pink-800' : 'bg-neutral-100 text-neutral-500'}`}>
        {count}
      </span>
    )}
  </button>
);

const AlertCard = ({ alert }) => {
  const config = {
    critical: { bg: 'bg-red-50', border: 'border-red-200', icon: AlertTriangle, iconColor: 'text-red-500' },
    warning: { bg: 'bg-amber-50', border: 'border-amber-200', icon: AlertCircle, iconColor: 'text-amber-500' },
    info: { bg: 'bg-blue-50', border: 'border-blue-200', icon: CheckCircle2, iconColor: 'text-blue-500' }
  };
  const { bg, border, icon: Icon, iconColor } = config[alert.type] || config.warning;
  return (
    <div className={`flex items-start gap-3 p-3 rounded-xl border ${bg} ${border}`}>
      <Icon className={`w-5 h-5 ${iconColor} flex-shrink-0 mt-0.5`} />
      <p className="text-sm text-neutral-700">{alert.message}</p>
    </div>
  );
};

// ─── POC Cell with inline editing ─────────────────────────────

const POCCell = ({ cell, department, clientId, employeeOptions, onAssign, isLoading }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [selectedPoc, setSelectedPoc] = useState(cell.poc_id || '');

  const handleSave = () => {
    onAssign(clientId, department, selectedPoc || null);
    setIsEditing(false);
  };

  if (cell.status === 'not_applicable') {
    return <td className="px-2 py-2 text-center"><span className="text-neutral-300">—</span></td>;
  }

  if (isEditing) {
    return (
      <td className="px-2 py-2">
        <div className="flex items-center gap-1">
          <select
            value={selectedPoc}
            onChange={(e) => setSelectedPoc(e.target.value)}
            className="text-xs border border-white/40 rounded px-1.5 py-1 w-full max-w-[120px] focus:ring-1 focus:ring-pink-400 focus:border-pink-400"
            autoFocus
          >
            <option value="">Unassign</option>
            {employeeOptions.map(emp => (
              <option key={emp.id} value={emp.id}>{emp.name}</option>
            ))}
          </select>
          <button onClick={handleSave} className="p-0.5 text-green-600 hover:bg-green-50 rounded" disabled={isLoading}>
            <Check className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => { setSelectedPoc(cell.poc_id || ''); setIsEditing(false); }} className="p-0.5 text-neutral-400 hover:bg-neutral-100 rounded">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </td>
    );
  }

  return (
    <td className="px-2 py-2">
      <button
        onClick={() => setIsEditing(true)}
        className={`w-full text-left px-2 py-1 rounded text-xs transition-colors font-medium ${
          cell.status === 'assigned'
            ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200'
            : 'bg-amber-50 text-amber-600 hover:bg-amber-100 border border-amber-200'
        }`}
      >
        {cell.status === 'assigned' ? cell.poc_name : '+ Assign'}
      </button>
    </td>
  );
};

// ─── POC Tally Tab ────────────────────────────────────────────

const POCTallyTab = ({ tallyData, loading }) => {
  const [sortConfig, setSortConfig] = useState({ key: 'total_clients', direction: 'desc' });
  const [expandedRow, setExpandedRow] = useState(null);
  const [filterDept, setFilterDept] = useState('');
  const [hideEmpty, setHideEmpty] = useState(false);

  const handleSort = useCallback((key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  }, []);

  const sortedRows = useMemo(() => {
    if (!tallyData?.rows) return [];
    let rows = [...tallyData.rows];

    if (hideEmpty) rows = rows.filter(r => r.total_clients > 0);
    if (filterDept) rows = rows.filter(r => r.departments[filterDept]?.count > 0);

    rows.sort((a, b) => {
      let aVal, bVal;
      if (sortConfig.key === 'employee_name') {
        aVal = a.employee_name; bVal = b.employee_name;
        const cmp = aVal.localeCompare(bVal);
        return sortConfig.direction === 'asc' ? cmp : -cmp;
      } else if (sortConfig.key === 'primary_department') {
        aVal = a.primary_department; bVal = b.primary_department;
        const cmp = aVal.localeCompare(bVal);
        return sortConfig.direction === 'asc' ? cmp : -cmp;
      } else if (sortConfig.key === 'total_clients') {
        aVal = a.total_clients; bVal = b.total_clients;
      } else {
        // Department-specific sort
        aVal = a.departments[sortConfig.key]?.count || 0;
        bVal = b.departments[sortConfig.key]?.count || 0;
      }
      return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
    });
    return rows;
  }, [tallyData, sortConfig, filterDept, hideEmpty]);

  if (loading || !tallyData) {
    return <div className="flex items-center justify-center h-40"><Loader2 className="w-6 h-6 animate-spin text-pink-500" /></div>;
  }

  const departments = tallyData.departments || [];
  const maxVal = tallyData.summary?.max_assignments || 1;

  const heatColor = (count) => {
    if (count === 0) return '';
    const intensity = Math.min(count / Math.max(maxVal, 1), 1);
    if (intensity > 0.7) return 'bg-pink-100 text-pink-800 font-bold';
    if (intensity > 0.4) return 'bg-pink-50 text-pink-700 font-semibold';
    return 'bg-white/40 text-neutral-700 font-medium';
  };

  return (
    <div className="space-y-4" data-testid="poc-tally-tab">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <select
          value={filterDept}
          onChange={(e) => setFilterDept(e.target.value)}
          className="text-sm border border-white/40 rounded-xl px-3 py-2 focus:ring-2 focus:ring-pink-400 focus:border-pink-400 bg-white"
          data-testid="tally-dept-filter"
        >
          <option value="">All Departments</option>
          {departments.map(d => (
            <option key={d} value={d}>{d}</option>
          ))}
        </select>
        <FilterChip label="Hide empty" active={hideEmpty} onClick={() => setHideEmpty(!hideEmpty)} />
        <span className="ml-auto text-xs text-neutral-400">
          {sortedRows.length} employee{sortedRows.length !== 1 ? 's' : ''} | {tallyData.summary?.grand_total_assignments || 0} total assignments
        </span>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="glass-card p-3">
          <p className="text-2xl font-bold text-[#1a1a2e]">{tallyData.summary?.employees_with_clients || 0}</p>
          <p className="text-xs text-neutral-500">Active POCs</p>
        </div>
        <div className="glass-card p-3">
          <p className="text-2xl font-bold text-[#1a1a2e]">{tallyData.summary?.grand_total_assignments || 0}</p>
          <p className="text-xs text-neutral-500">Total Assignments</p>
        </div>
        <div className="glass-card p-3">
          <p className="text-2xl font-bold text-[#1a1a2e]">{tallyData.summary?.employees_without_clients || 0}</p>
          <p className="text-xs text-neutral-500">Unassigned Staff</p>
        </div>
        <div className="glass-card p-3">
          <p className="text-2xl font-bold text-[#1a1a2e]">{tallyData.summary?.max_assignments || 0}</p>
          <p className="text-xs text-neutral-500">Max per Person</p>
        </div>
      </div>

      {/* Tally Table */}
      <div className="soft-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/40 border-b border-white/40">
              <tr>
                <th className="px-4 py-3 text-left w-8"></th>
                <th className="px-4 py-3 text-left min-w-[160px]">
                  <SortHeader label="Employee" sortKey="employee_name" sortConfig={sortConfig} onSort={handleSort} />
                </th>
                <th className="px-4 py-3 text-left min-w-[120px]">
                  <SortHeader label="Department" sortKey="primary_department" sortConfig={sortConfig} onSort={handleSort} />
                </th>
                {departments.map(dept => (
                  <th key={dept} className="px-3 py-3 text-center min-w-[90px]">
                    <SortHeader label={dept} sortKey={dept} sortConfig={sortConfig} onSort={handleSort} align="center" />
                  </th>
                ))}
                <th className="px-4 py-3 text-center min-w-[80px] bg-neutral-100/60">
                  <SortHeader label="Total" sortKey="total_clients" sortConfig={sortConfig} onSort={handleSort} align="center" />
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/30">
              {sortedRows.map((row) => (
                <React.Fragment key={row.employee_id}>
                  <tr className="hover:bg-neutral-50 transition-colors">
                    <td className="px-4 py-2.5">
                      {row.total_clients > 0 && (
                        <button
                          onClick={() => setExpandedRow(expandedRow === row.employee_id ? null : row.employee_id)}
                          className="p-0.5 hover:bg-neutral-200 rounded transition-colors"
                          data-testid={`expand-${row.employee_id}`}
                        >
                          {expandedRow === row.employee_id
                            ? <ChevronUp className="w-4 h-4 text-neutral-500" />
                            : <ChevronDown className="w-4 h-4 text-neutral-400" />}
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      <span className="font-medium text-[#1a1a2e]">{row.employee_name}</span>
                    </td>
                    <td className="px-4 py-2.5">
                      <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-neutral-100 text-neutral-600">
                        {row.primary_department}
                      </span>
                    </td>
                    {departments.map(dept => {
                      const count = row.departments[dept]?.count || 0;
                      return (
                        <td key={dept} className="px-3 py-2.5 text-center">
                          {count > 0 ? (
                            <span className={`inline-flex items-center justify-center w-8 h-8 rounded-xl text-sm ${heatColor(count)}`}>
                              {count}
                            </span>
                          ) : (
                            <span className="text-gray-200">-</span>
                          )}
                        </td>
                      );
                    })}
                    <td className="px-4 py-2.5 text-center bg-white/40">
                      <span className={`inline-flex items-center justify-center w-9 h-9 rounded-xl text-sm font-bold ${
                        row.total_clients > 0 ? 'bg-pink-100 text-pink-800 border border-pink-200' : 'text-neutral-300'
                      }`}>
                        {row.total_clients || '-'}
                      </span>
                    </td>
                  </tr>
                  {/* Expanded detail row */}
                  {expandedRow === row.employee_id && row.total_clients > 0 && (
                    <tr className="bg-white/40">
                      <td></td>
                      <td colSpan={departments.length + 3} className="px-4 py-3">
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                          {departments.map(dept => {
                            const clients = row.departments[dept]?.clients || [];
                            if (clients.length === 0) return null;
                            return (
                              <div key={dept} className="glass-card p-3">
                                <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-1.5">{dept}</p>
                                <div className="space-y-1">
                                  {clients.map((c, i) => (
                                    <p key={i} className="text-sm text-[#1a1a2e] flex items-center gap-1.5">
                                      <span className="w-1.5 h-1.5 rounded-full bg-pink-400 flex-shrink-0"></span>
                                      {c}
                                    </p>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
            <tfoot className="bg-white/40 border-t-2 border-white/40">
              <tr>
                <td></td>
                <td colSpan={2} className="px-4 py-2.5 font-bold text-neutral-700 text-xs uppercase tracking-wide">Grand Total</td>
                {departments.map(dept => (
                  <td key={dept} className="px-3 py-2.5 text-center">
                    <span className="font-bold text-neutral-700">{tallyData.department_totals?.[dept] || 0}</span>
                  </td>
                ))}
                <td className="px-4 py-2.5 text-center bg-neutral-100/60">
                  <span className="font-bold text-pink-700 text-base">{tallyData.summary?.grand_total_assignments || 0}</span>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Heatmap legend */}
      <div className="flex items-center gap-4 text-xs text-neutral-500">
        <span className="font-medium">Heatmap:</span>
        <div className="flex items-center gap-1.5"><span className="w-5 h-5 rounded bg-white/40 border border-white/40"></span> Low</div>
        <div className="flex items-center gap-1.5"><span className="w-5 h-5 rounded bg-pink-50 border border-pink-200"></span> Medium</div>
        <div className="flex items-center gap-1.5"><span className="w-5 h-5 rounded bg-pink-100 border border-pink-300"></span> High</div>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────

export default function ClientDistributionManager() {
  const [activeTab, setActiveTab] = useState('matrix');
  const [matrixData, setMatrixData] = useState(null);
  const [workloadData, setWorkloadData] = useState(null);
  const [tallyData, setTallyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tallyLoading, setTallyLoading] = useState(false);
  const [assigningPoc, setAssigningPoc] = useState(false);

  // Matrix filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCoverage, setFilterCoverage] = useState(''); // '', 'needs_poc', 'fully_assigned', 'no_departments'
  const [filterCategory, setFilterCategory] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('');
  const [filterPoc, setFilterPoc] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'client_name', direction: 'asc' });

  // Selection
  const [selectedClients, setSelectedClients] = useState([]);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkAction, setBulkAction] = useState({ department: '', poc_id: '' });

  const activeFilterCount = [filterCoverage, filterCategory, filterDepartment, filterPoc, searchTerm].filter(Boolean).length;

  // Fetch core data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [matrixRes, workloadRes] = await Promise.all([
        adminApi.get('/api/admin/distribution/matrix'),
        adminApi.get('/api/admin/distribution/workload')
      ]);
      setMatrixData(matrixRes.data);
      setWorkloadData(workloadRes.data);
    } catch (error) {
      toast.error('Failed to load distribution data');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch tally data (lazy - only when tab is opened)
  const fetchTally = useCallback(async () => {
    if (tallyData) return; // already loaded
    setTallyLoading(true);
    try {
      const res = await adminApi.get('/api/admin/distribution/poc-tally');
      setTallyData(res.data);
    } catch (error) {
      toast.error('Failed to load POC tally');
    } finally {
      setTallyLoading(false);
    }
  }, [tallyData]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (activeTab === 'tally') fetchTally();
  }, [activeTab, fetchTally]);

  const handleRefresh = () => {
    setTallyData(null);
    fetchData();
    if (activeTab === 'tally') {
      setTimeout(() => fetchTally(), 500);
    }
  };

  // POC assignment
  const handleAssignPoc = async (clientId, department, pocId) => {
    setAssigningPoc(true);
    try {
      await adminApi.put('/api/admin/distribution/assign-poc', {
        client_id: clientId, department, poc_id: pocId
      });
      toast.success('POC updated');
      setTallyData(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign POC');
    } finally {
      setAssigningPoc(false);
    }
  };

  // Bulk assignment
  const handleBulkAssign = async () => {
    if (!bulkAction.department) return toast.error('Please select a department');
    setAssigningPoc(true);
    try {
      const assignments = selectedClients.map(clientId => ({
        client_id: clientId, department: bulkAction.department, poc_id: bulkAction.poc_id || null
      }));
      const res = await adminApi.post('/api/admin/distribution/bulk-assign', { assignments });
      toast.success(`Updated ${res.data.successful} clients`);
      if (res.data.failed > 0) toast.warning(`${res.data.failed} assignments failed`);
      setShowBulkModal(false); setSelectedClients([]); setBulkAction({ department: '', poc_id: '' });
      setTallyData(null);
      fetchData();
    } catch {
      toast.error('Failed to perform bulk assignment');
    } finally {
      setAssigningPoc(false);
    }
  };

  // Sort handler
  const handleSort = useCallback((key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  }, []);

  // Filter + sort matrix
  const filteredMatrix = useMemo(() => {
    if (!matrixData?.matrix) return [];
    let rows = [...matrixData.matrix];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      rows = rows.filter(r => r.client_name.toLowerCase().includes(term));
    }
    if (filterCategory) rows = rows.filter(r => r.client_category === filterCategory);
    if (filterDepartment) rows = rows.filter(r => r.cells[filterDepartment]?.status !== 'not_applicable');
    if (filterPoc) rows = rows.filter(r => Object.values(r.cells).some(c => c.poc_id === filterPoc));

    if (filterCoverage === 'needs_poc') {
      rows = rows.filter(r => Object.values(r.cells).some(c => c.status === 'needs_poc'));
    } else if (filterCoverage === 'fully_assigned') {
      rows = rows.filter(r => r.department_count > 0 && !Object.values(r.cells).some(c => c.status === 'needs_poc'));
    } else if (filterCoverage === 'no_departments') {
      rows = rows.filter(r => r.department_count === 0);
    }

    // Sort
    const departments = matrixData.departments || [];
    rows.sort((a, b) => {
      const { key, direction } = sortConfig;
      let cmp = 0;

      if (key === 'client_name') {
        cmp = (a.client_name || '').localeCompare(b.client_name || '');
      } else if (key === 'department_count') {
        cmp = a.department_count - b.department_count;
      } else if (departments.includes(key)) {
        // Sort by cell status: assigned=2, needs_poc=1, not_applicable=0
        const statusVal = (s) => s === 'assigned' ? 2 : s === 'needs_poc' ? 1 : 0;
        cmp = statusVal(a.cells[key]?.status) - statusVal(b.cells[key]?.status);
      }
      return direction === 'asc' ? cmp : -cmp;
    });
    return rows;
  }, [matrixData, searchTerm, filterCategory, filterDepartment, filterPoc, filterCoverage, sortConfig]);

  const clearAllFilters = () => {
    setSearchTerm(''); setFilterCoverage(''); setFilterCategory('');
    setFilterDepartment(''); setFilterPoc('');
  };

  const toggleClientSelection = (clientId) => {
    setSelectedClients(prev => prev.includes(clientId) ? prev.filter(id => id !== clientId) : [...prev, clientId]);
  };
  const toggleSelectAll = () => {
    setSelectedClients(prev => prev.length === filteredMatrix.length ? [] : filteredMatrix.map(r => r.client_id));
  };

  // CSV Export
  const exportToCSV = () => {
    if (!matrixData) return;
    const headers = ['Client Name', 'Category', ...matrixData.departments, 'Dept Count'];
    const csvRows = filteredMatrix.map(row => [
      `"${row.client_name}"`, row.client_category || '',
      ...matrixData.departments.map(d => {
        const c = row.cells[d];
        return c.status === 'not_applicable' ? '-' : c.status === 'assigned' ? c.poc_name : 'Unassigned';
      }),
      row.department_count
    ]);
    const csv = [headers, ...csvRows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url;
    a.download = `client_distribution_${getTodayLocal()}.csv`;
    a.click(); URL.revokeObjectURL(url);
    toast.success('CSV exported');
  };

  if (loading) {
    return (
      <div className="space-y-5" data-testid="client-distribution-loading">
        <HeaderSkeleton />
        <MetricCardsSkeleton count={4} />
        <TableSkeleton rows={6} columns={5} />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="client-distribution-manager">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-[#1a1a2e]">Client Distribution & Workload</h2>
          <p className="text-sm text-neutral-500 mt-0.5">Manage assignments, monitor workload, audit POC coverage</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleRefresh} className="p-2 text-neutral-500 hover:bg-neutral-100 rounded-xl transition-colors" title="Refresh" data-testid="refresh-btn">
            <RefreshCw className="w-5 h-5" />
          </button>
          <button onClick={exportToCSV} className="p-2 text-neutral-500 hover:bg-neutral-100 rounded-xl transition-colors" title="Export CSV" data-testid="export-csv-btn">
            <Download className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="glass-card p-4 flex items-center gap-3">
          <div className="p-2 bg-pink-50 rounded-xl"><Users className="w-5 h-5 text-pink-600" /></div>
          <div>
            <p className="text-2xl font-bold text-[#1a1a2e]">{matrixData?.summary?.total_clients || 0}</p>
            <p className="text-xs text-neutral-500">Total Clients</p>
          </div>
        </div>
        <div className="glass-card p-4 flex items-center gap-3">
          <div className="p-2 bg-blue-50 rounded-xl"><Building2 className="w-5 h-5 text-blue-600" /></div>
          <div>
            <p className="text-2xl font-bold text-[#1a1a2e]">{matrixData?.summary?.total_departments || 0}</p>
            <p className="text-xs text-neutral-500">Departments</p>
          </div>
        </div>
        <div className="glass-card p-4 flex items-center gap-3">
          <div className="p-2 bg-amber-50 rounded-xl"><AlertTriangle className="w-5 h-5 text-amber-600" /></div>
          <div>
            <p className="text-2xl font-bold text-[#1a1a2e]">{matrixData?.summary?.clients_needing_poc || 0}</p>
            <p className="text-xs text-neutral-500">Need POC</p>
          </div>
        </div>
        <div className="glass-card p-4 flex items-center gap-3">
          <div className="p-2 bg-red-50 rounded-xl"><TrendingUp className="w-5 h-5 text-red-600" /></div>
          <div>
            <p className="text-2xl font-bold text-[#1a1a2e]">{workloadData?.summary?.high_workload_count || 0}</p>
            <p className="text-xs text-neutral-500">High Workload</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-white/40">
        <nav className="flex gap-1" data-testid="distribution-tabs">
          {[
            { key: 'matrix', label: 'Client-Department Matrix' },
            { key: 'tally', label: 'POC Tally' },
            { key: 'workload', label: 'Employee Workload' },
            { key: 'alerts', label: 'Alerts & Insights', badge: workloadData?.alerts?.length }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              data-testid={`tab-${tab.key}`}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-pink-500 text-pink-600'
                  : 'border-transparent text-neutral-500 hover:text-neutral-700 hover:border-white/40'
              }`}
            >
              {tab.label}
              {tab.badge > 0 && (
                <span className="ml-2 px-2 py-0.5 text-xs bg-red-100 text-red-600 rounded-full">{tab.badge}</span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* ─── MATRIX TAB ─── */}
      {activeTab === 'matrix' && (
        <div className="space-y-4" data-testid="matrix-tab">
          {/* Filter Bar */}
          <div className="glass-card p-4 space-y-3">
            <div className="flex flex-wrap gap-3 items-center">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                <input
                  type="text"
                  placeholder="Search clients..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 pr-4 py-2 text-sm border border-white/40 rounded-xl w-52 focus:ring-2 focus:ring-pink-400 focus:border-pink-400"
                  data-testid="search-clients"
                />
              </div>

              {/* Department Filter */}
              <select
                value={filterDepartment}
                onChange={(e) => setFilterDepartment(e.target.value)}
                className="text-sm border border-white/40 rounded-xl px-3 py-2 focus:ring-2 focus:ring-pink-400 focus:border-pink-400 bg-white"
                data-testid="filter-department"
              >
                <option value="">All Departments</option>
                {matrixData?.departments?.map(d => (
                  <option key={d} value={d}>{d} ({matrixData.department_totals?.[d]?.clients || 0})</option>
                ))}
              </select>

              {/* Category Filter */}
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="text-sm border border-white/40 rounded-xl px-3 py-2 focus:ring-2 focus:ring-pink-400 focus:border-pink-400 bg-white"
                data-testid="filter-category"
              >
                <option value="">All Categories</option>
                {matrixData?.categories?.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>

              {/* POC Filter */}
              <select
                value={filterPoc}
                onChange={(e) => setFilterPoc(e.target.value)}
                className="text-sm border border-white/40 rounded-xl px-3 py-2 focus:ring-2 focus:ring-pink-400 focus:border-pink-400 bg-white"
                data-testid="filter-poc"
              >
                <option value="">All POCs</option>
                {matrixData?.poc_filter_options?.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>

              {/* Clear All */}
              {activeFilterCount > 0 && (
                <button
                  onClick={clearAllFilters}
                  className="inline-flex items-center gap-1 px-3 py-2 text-xs font-medium text-red-600 hover:bg-red-50 rounded-xl transition-colors"
                  data-testid="clear-filters"
                >
                  <XCircle className="w-3.5 h-3.5" />
                  Clear all ({activeFilterCount})
                </button>
              )}
            </div>

            {/* Coverage Chips */}
            <div className="flex flex-wrap gap-2 items-center">
              <span className="text-xs text-neutral-400 font-medium mr-1">Coverage:</span>
              <FilterChip label="All" active={!filterCoverage} onClick={() => setFilterCoverage('')} count={matrixData?.summary?.total_clients} />
              <FilterChip label="Needs POC" active={filterCoverage === 'needs_poc'} onClick={() => setFilterCoverage(filterCoverage === 'needs_poc' ? '' : 'needs_poc')} count={matrixData?.summary?.clients_needing_poc} />
              <FilterChip label="Fully Assigned" active={filterCoverage === 'fully_assigned'} onClick={() => setFilterCoverage(filterCoverage === 'fully_assigned' ? '' : 'fully_assigned')} count={matrixData?.summary?.fully_assigned} />
              <FilterChip label="No Departments" active={filterCoverage === 'no_departments'} onClick={() => setFilterCoverage(filterCoverage === 'no_departments' ? '' : 'no_departments')} count={matrixData?.summary?.no_departments} />

              {/* Bulk action */}
              {selectedClients.length > 0 && (
                <button
                  onClick={() => setShowBulkModal(true)}
                  className="ml-auto flex items-center gap-1.5 px-4 py-1.5 bg-pink-600 text-white text-xs font-semibold rounded-xl hover:bg-pink-700 transition-colors"
                  data-testid="bulk-assign-btn"
                >
                  <UserPlus className="w-3.5 h-3.5" />
                  Bulk Assign ({selectedClients.length})
                </button>
              )}
            </div>
          </div>

          {/* Results count */}
          <div className="flex items-center justify-between">
            <p className="text-xs text-neutral-400">
              Showing {filteredMatrix.length} of {matrixData?.matrix?.length || 0} clients
            </p>
          </div>

          {/* Matrix Table */}
          <div className="soft-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="matrix-table">
                <thead className="bg-white/40 border-b border-white/40">
                  <tr>
                    <th className="px-3 py-3 text-left w-10">
                      <input
                        type="checkbox"
                        checked={selectedClients.length === filteredMatrix.length && filteredMatrix.length > 0}
                        onChange={toggleSelectAll}
                        className="rounded border-white/40 text-pink-600 focus:ring-pink-500"
                        data-testid="select-all"
                      />
                    </th>
                    <th className="px-3 py-3 text-left min-w-[180px]">
                      <SortHeader label="Client Name" sortKey="client_name" sortConfig={sortConfig} onSort={handleSort} />
                    </th>
                    {matrixData?.departments?.map(dept => (
                      <th key={dept} className="px-2 py-3 text-center min-w-[100px]">
                        <SortHeader label={dept} sortKey={dept} sortConfig={sortConfig} onSort={handleSort} align="center" />
                        <span className="block text-[10px] font-normal text-neutral-400 mt-0.5">
                          ({matrixData.department_totals?.[dept]?.clients || 0})
                        </span>
                      </th>
                    ))}
                    <th className="px-3 py-3 text-center w-16">
                      <SortHeader label="Depts" sortKey="department_count" sortConfig={sortConfig} onSort={handleSort} align="center" />
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/30">
                  {filteredMatrix.length === 0 ? (
                    <tr>
                      <td colSpan={(matrixData?.departments?.length || 0) + 3} className="px-6 py-12 text-center">
                        <div className="text-neutral-400">
                          <Search className="w-8 h-8 mx-auto mb-2 opacity-50" />
                          <p className="font-medium">No clients match your filters</p>
                          <button onClick={clearAllFilters} className="mt-2 text-pink-600 text-sm hover:underline">Clear all filters</button>
                        </div>
                      </td>
                    </tr>
                  ) : filteredMatrix.map(row => (
                    <tr key={row.client_id} className="hover:bg-neutral-50 transition-colors" data-testid={`matrix-row-${row.client_id}`}>
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={selectedClients.includes(row.client_id)}
                          onChange={() => toggleClientSelection(row.client_id)}
                          className="rounded border-white/40 text-pink-600 focus:ring-pink-500"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          {row.client_color && (
                            <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: row.client_color }} />
                          )}
                          <span className="font-medium text-[#1a1a2e]">{row.client_name}</span>
                          {row.client_category && (
                            <span className="text-[10px] px-1.5 py-0.5 bg-neutral-100 text-neutral-500 rounded hidden lg:inline">
                              {row.client_category}
                            </span>
                          )}
                        </div>
                      </td>
                      {matrixData?.departments?.map(dept => (
                        <POCCell
                          key={`${row.client_id}-${dept}`}
                          cell={row.cells[dept]}
                          department={dept}
                          clientId={row.client_id}
                          employeeOptions={matrixData.employees_by_department?.[dept] || []}
                          onAssign={handleAssignPoc}
                          isLoading={assigningPoc}
                        />
                      ))}
                      <td className="px-3 py-2 text-center">
                        <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold ${
                          row.department_count === 0 ? 'bg-red-50 text-red-500' : 'bg-neutral-100 text-neutral-600'
                        }`}>
                          {row.department_count}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
                {filteredMatrix.length > 0 && (
                  <tfoot className="bg-white/40 border-t-2 border-white/40">
                    <tr>
                      <td></td>
                      <td className="px-3 py-2.5 font-bold text-neutral-700 text-xs uppercase tracking-wide">Totals</td>
                      {matrixData?.departments?.map(dept => (
                        <td key={dept} className="px-2 py-2.5 text-center">
                          <div className="flex flex-col items-center">
                            <span className="font-bold text-neutral-700">{matrixData.department_totals?.[dept]?.clients || 0}</span>
                            <span className="text-[10px] text-neutral-400">{matrixData.department_totals?.[dept]?.with_poc || 0} assigned</span>
                          </div>
                        </td>
                      ))}
                      <td className="px-3 py-2.5 text-center font-bold text-neutral-700">{filteredMatrix.length}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>

          {/* Legend */}
          <div className="flex flex-wrap gap-5 text-xs text-neutral-500">
            <div className="flex items-center gap-2"><div className="w-4 h-4 bg-emerald-50 border border-emerald-200 rounded"></div> POC Assigned</div>
            <div className="flex items-center gap-2"><div className="w-4 h-4 bg-amber-50 border border-amber-200 rounded"></div> Needs POC</div>
            <div className="flex items-center gap-2"><span className="text-neutral-300">—</span> Not in Department</div>
          </div>
        </div>
      )}

      {/* ─── POC TALLY TAB ─── */}
      {activeTab === 'tally' && (
        <POCTallyTab tallyData={tallyData} loading={tallyLoading} />
      )}

      {/* ─── WORKLOAD TAB ─── */}
      {activeTab === 'workload' && (
        <div className="space-y-6" data-testid="workload-tab">
          <div className="soft-card overflow-hidden">
            <div className="px-4 py-3 border-b border-white/40 bg-white/40">
              <h3 className="font-semibold text-[#1a1a2e]">Employee Workload Metrics</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-white/40 border-b border-white/40">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold text-neutral-600 text-xs uppercase tracking-wide">Employee</th>
                    <th className="px-4 py-3 text-left font-semibold text-neutral-600 text-xs uppercase tracking-wide">Department</th>
                    <th className="px-4 py-3 text-center font-semibold text-neutral-600 text-xs uppercase tracking-wide">Clients</th>
                    <th className="px-4 py-3 text-center font-semibold text-neutral-600 text-xs uppercase tracking-wide">Created</th>
                    <th className="px-4 py-3 text-center font-semibold text-neutral-600 text-xs uppercase tracking-wide">Assigned</th>
                    <th className="px-4 py-3 text-center font-semibold text-neutral-600 text-xs uppercase tracking-wide">Done</th>
                    <th className="px-4 py-3 text-center font-semibold text-neutral-600 text-xs uppercase tracking-wide">Overdue</th>
                    <th className="px-4 py-3 text-center font-semibold text-neutral-600 text-xs uppercase tracking-wide">Completion</th>
                    <th className="px-4 py-3 text-center font-semibold text-neutral-600 text-xs uppercase tracking-wide">Workload</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/30">
                  {workloadData?.employees?.map(emp => (
                    <tr key={emp.employee_id} className="hover:bg-neutral-50 transition-colors">
                      <td className="px-4 py-3">
                        <p className="font-medium text-[#1a1a2e]">{emp.employee_name}</p>
                        {emp.designation && <p className="text-xs text-neutral-400">{emp.designation}</p>}
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-neutral-100 text-neutral-600">{emp.department}</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-semibold ${
                          emp.clients_as_poc > 3 ? 'bg-red-100 text-red-700' : emp.clients_as_poc > 1 ? 'bg-amber-100 text-amber-700' : 'bg-neutral-100 text-neutral-600'
                        }`}>{emp.clients_as_poc}</span>
                      </td>
                      <td className="px-4 py-3 text-center text-neutral-600">{emp.tasks_created}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`font-medium ${emp.tasks_assigned > 15 ? 'text-red-600' : emp.tasks_assigned > 8 ? 'text-amber-600' : 'text-neutral-700'}`}>{emp.tasks_assigned}</span>
                      </td>
                      <td className="px-4 py-3 text-center text-emerald-600 font-medium">{emp.tasks_completed}</td>
                      <td className="px-4 py-3 text-center">
                        {emp.tasks_overdue > 0 ? <span className="text-red-600 font-medium">{emp.tasks_overdue}</span> : <span className="text-neutral-300">0</span>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <ProgressBar value={emp.completion_rate} color={emp.completion_rate >= 70 ? 'green' : emp.completion_rate >= 40 ? 'yellow' : 'red'} />
                          <span className="text-xs text-neutral-500 w-10">{emp.completion_rate}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <WorkloadBadge level={emp.workload_level} score={emp.workload_score} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Department Summary */}
          <div className="soft-card overflow-hidden">
            <div className="px-4 py-3 border-b border-white/40 bg-white/40">
              <h3 className="font-semibold text-[#1a1a2e]">Department Summary</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-white/40 border-b border-white/40">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold text-neutral-600 text-xs uppercase tracking-wide">Department</th>
                    <th className="px-4 py-3 text-center font-semibold text-neutral-600 text-xs uppercase tracking-wide">Employees</th>
                    <th className="px-4 py-3 text-center font-semibold text-neutral-600 text-xs uppercase tracking-wide">Clients</th>
                    <th className="px-4 py-3 text-center font-semibold text-neutral-600 text-xs uppercase tracking-wide">Tasks</th>
                    <th className="px-4 py-3 text-center font-semibold text-neutral-600 text-xs uppercase tracking-wide">Done</th>
                    <th className="px-4 py-3 text-center font-semibold text-neutral-600 text-xs uppercase tracking-wide">Overdue</th>
                    <th className="px-4 py-3 text-center font-semibold text-neutral-600 text-xs uppercase tracking-wide">Completion</th>
                    <th className="px-4 py-3 text-center font-semibold text-neutral-600 text-xs uppercase tracking-wide">Workload</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/30">
                  {workloadData?.departments?.map(dept => (
                    <tr key={dept.department} className="hover:bg-neutral-50 transition-colors">
                      <td className="px-4 py-3 font-medium text-[#1a1a2e]">{dept.department}</td>
                      <td className="px-4 py-3 text-center text-neutral-600">{dept.employees}</td>
                      <td className="px-4 py-3 text-center text-neutral-600">{dept.clients}</td>
                      <td className="px-4 py-3 text-center text-neutral-600">{dept.tasks_assigned}</td>
                      <td className="px-4 py-3 text-center text-emerald-600 font-medium">{dept.tasks_completed}</td>
                      <td className="px-4 py-3 text-center">
                        {dept.tasks_overdue > 0 ? <span className="text-red-600 font-medium">{dept.tasks_overdue}</span> : <span className="text-neutral-300">0</span>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <ProgressBar value={dept.completion_rate} color={dept.completion_rate >= 70 ? 'green' : dept.completion_rate >= 40 ? 'yellow' : 'red'} />
                          <span className="text-xs text-neutral-500 w-10">{dept.completion_rate}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <WorkloadBadge level={dept.workload_level} score={dept.workload_score} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Formula */}
          <div className="bg-white/40 rounded-xl p-4 text-sm">
            <h4 className="font-medium text-neutral-700 mb-2">Workload Score Formula</h4>
            <p className="text-neutral-600">Score = (Active Tasks x 2) + (Clients as POC x 3) + (Overdue Tasks x 5)</p>
            <div className="flex gap-4 mt-2 text-xs">
              <span>Low: 0-15</span>
              <span>Medium: 16-30</span>
              <span>High: 31+</span>
            </div>
          </div>
        </div>
      )}

      {/* ─── ALERTS TAB ─── */}
      {activeTab === 'alerts' && (
        <div className="space-y-4" data-testid="alerts-tab">
          {workloadData?.alerts?.length > 0 ? (
            <div className="space-y-3">
              {workloadData.alerts.map((alert, idx) => <AlertCard key={idx} alert={alert} />)}
            </div>
          ) : (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-8 text-center">
              <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
              <h3 className="font-semibold text-emerald-700">All Clear!</h3>
              <p className="text-sm text-emerald-600 mt-1">No workload issues detected.</p>
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            <div className="glass-card p-4 text-center">
              <p className="text-2xl font-bold text-[#1a1a2e]">{workloadData?.summary?.avg_completion_rate || 0}%</p>
              <p className="text-xs text-neutral-500 mt-1">Avg Completion Rate</p>
            </div>
            <div className="glass-card p-4 text-center">
              <p className="text-2xl font-bold text-[#1a1a2e]">{workloadData?.summary?.total_overdue_tasks || 0}</p>
              <p className="text-xs text-neutral-500 mt-1">Total Overdue Tasks</p>
            </div>
            <div className="glass-card p-4 text-center">
              <p className="text-2xl font-bold text-[#1a1a2e]">{workloadData?.summary?.high_workload_count || 0}</p>
              <p className="text-xs text-neutral-500 mt-1">High Workload Employees</p>
            </div>
            <div className="glass-card p-4 text-center">
              <p className="text-2xl font-bold text-[#1a1a2e]">{matrixData?.summary?.clients_needing_poc || 0}</p>
              <p className="text-xs text-neutral-500 mt-1">Clients Need POC</p>
            </div>
          </div>
        </div>
      )}

      {/* ─── BULK ASSIGN MODAL ─── */}
      {showBulkModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="bulk-modal">
          <div className="bg-white rounded-xl w-full max-w-md mx-4 shadow-2xl">
            <div className="flex items-center justify-between p-4 border-b border-white/40">
              <h2 className="text-lg font-semibold text-[#1a1a2e]">Bulk Assign POC ({selectedClients.length} clients)</h2>
              <button onClick={() => setShowBulkModal(false)} className="p-1 hover:bg-neutral-100 rounded-xl">
                <X className="w-5 h-5 text-neutral-500" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Department <span className="text-red-500">*</span></label>
                <select
                  value={bulkAction.department}
                  onChange={(e) => setBulkAction(prev => ({ ...prev, department: e.target.value, poc_id: '' }))}
                  className="w-full px-3 py-2 border border-white/40 rounded-xl focus:outline-none focus:ring-2 focus:ring-brutal-pink/30 focus:border-brutal-pink/50"
                >
                  <option value="">Select Department</option>
                  {matrixData?.departments?.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              {bulkAction.department && (
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Assign POC</label>
                  <select
                    value={bulkAction.poc_id}
                    onChange={(e) => setBulkAction(prev => ({ ...prev, poc_id: e.target.value }))}
                    className="w-full px-3 py-2 border border-white/40 rounded-xl focus:outline-none focus:ring-2 focus:ring-brutal-pink/30 focus:border-brutal-pink/50"
                  >
                    <option value="">Remove POC (Unassign)</option>
                    {matrixData?.employees_by_department?.[bulkAction.department]?.map(emp => (
                      <option key={emp.id} value={emp.id}>{emp.name}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                <p className="text-xs text-amber-700">
                  <strong>Note:</strong> Only updates clients already in the selected department. Others will be skipped.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3 p-4 border-t border-white/40">
              <button onClick={() => setShowBulkModal(false)} className="px-4 py-2 text-sm text-neutral-600 hover:bg-neutral-100 rounded-xl">Cancel</button>
              <button
                onClick={handleBulkAssign}
                disabled={!bulkAction.department || assigningPoc}
                className="px-4 py-2 text-sm bg-pink-600 text-white rounded-xl hover:bg-pink-700 disabled:opacity-50 flex items-center gap-2"
              >
                {assigningPoc && <Loader2 className="w-4 h-4 animate-spin" />}
                Apply to {selectedClients.length} Clients
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
